import type { SQLiteDatabase } from 'expo-sqlite';
import { getRawDatabase } from './singleton';
import type {
  DatabaseAdapter,
  SqlParams,
  SqlRunResult,
} from './types';

/**
 * Implementación concreta usando expo-sqlite.
 * Si mañana cambia el nombre de un método, solo tocas este archivo.
 */
class ExpoSQLiteAdapter implements DatabaseAdapter {
    constructor(private readonly db: SQLiteDatabase) {}

    exec({ sql, sync }: { sql: string; sync?: boolean }) {
        if (sync) {
            // ⚠️ Sync: solo para casos MUY específicos, puede bloquear el JS thread
            return this.db.execSync(sql)
        }
        return this.db.execAsync(sql)
    }

    async run({ sql, params, sync }: { sql: string; params?: SqlParams; sync?: boolean }): Promise<SqlRunResult> {
        const bindParams = normalizeParams(params)

        if (sync) {

            if (Array.isArray(bindParams)) {
                const result = this.db.runSync(sql, ...(bindParams as any))
                return { lastInsertRowId: result.lastInsertRowId, changes: result.changes }
            } else if (bindParams) {
                const result = this.db.runSync(sql, bindParams as any)
                return { lastInsertRowId: result.lastInsertRowId, changes: result.changes }
            } else {
                const result = this.db.runSync(sql)
                return { lastInsertRowId: result.lastInsertRowId, changes: result.changes }
            }
        }

        // Async
        if (Array.isArray(bindParams)) {
            const result = await this.db.runAsync(sql, ...(bindParams as any))
            return { lastInsertRowId: result.lastInsertRowId, changes: result.changes }
        } else if (bindParams) {
            // objeto de named params { $id: 1 } o similar
            const result = await this.db.runAsync(sql, bindParams as any)
            return { lastInsertRowId: result.lastInsertRowId, changes: result.changes }
        } else {
            const result = await this.db.runAsync(sql)
            return { lastInsertRowId: result.lastInsertRowId, changes: result.changes }
        }
    }

    async getAll<T = any>({ sql, params, sync }: { sql: string; params?: SqlParams; sync?: boolean }): Promise<T[]> {
        const bindParams = normalizeParams(params)

        if (sync) {
            return this.db.getAllSync<T>(sql, bindParams as any)
        }

        return this.db.getAllAsync<T>(sql, bindParams as any)
    }

    async getFirst<T = any>({ sql, params, sync }: { sql: string; params?: SqlParams; sync?: boolean }): Promise<T | null> {
        const bindParams = normalizeParams(params)

        if (sync) {
            return this.db.getFirstSync<T>(sql, bindParams as any) ?? null
        }

        const row = await this.db.getFirstAsync<T>(sql, bindParams as any)
        return row ?? null
    }

    async transaction<T>(
        task: (db: DatabaseAdapter) => Promise<T>
    ): Promise<T> {
        let value: T | undefined

        await this.db.withExclusiveTransactionAsync(async (txn) => {
            const txAdapter = new ExpoSQLiteAdapter(txn as SQLiteDatabase)
            value = await task(txAdapter)
        })

        // Si task tiró error, la Promise se rechaza y nunca llegás acá.
        // Si no tiró error, value debería estar seteado.
        if (value === undefined) {
            throw new Error('Transaction task did not return a value')
        }

        return value

        // Usamos withExclusiveTransactionAsync para evitar problemas de locking
        // return this.db.withExclusiveTransactionAsync(async (txn) => {
        //     const txAdapter = new ExpoSQLiteAdapter(txn as SQLiteDatabase)
        //     return task(txAdapter)
        // })
    }
}

/**
 * Singleton del adapter.
 * En tus repositorios/imports, usá SIEMPRE getDb().
 */
let adapterPromise: Promise<DatabaseAdapter> | null = null;

export async function getDb(): Promise<DatabaseAdapter> {
  if (!adapterPromise) {
    adapterPromise = (async () => {
      const rawDb = await getRawDatabase();
      return new ExpoSQLiteAdapter(rawDb);
    })();
  }
  return adapterPromise;
}

function normalizeValue(value: any): string | number | null {
//   if (value === undefined) return null;
//   if (value === null) return null;
  if (value === undefined || value === null) return 0; // o '' sqlite no acepta null, rompe

  const t = typeof value;

  if (t === 'string' || t === 'number') return value;

  if (t === 'boolean') return value ? 1 : 0; // o 'true'/'false'

  if (value instanceof Date) return value.toISOString();

  // Arrays u objetos → los guardamos como JSON
  if (t === 'object') return JSON.stringify(value);

  // fallback
  return String(value);
}

/**
 * Convierte nuestros tipos de parámetros genéricos al tipo que
 * entiende expo-sqlite (arrays o objetos).
 */
function normalizeParams(params?: any): any {
  if (!params) return undefined;

  // Caso array de valores posicionales
  if (Array.isArray(params)) {
    return params.map((v) => normalizeValue(v));
  }

  // Caso object con named params { :id: 1, :name: '...' }
  if (typeof params === 'object') {
    const result: Record<string, string | number | null> = {};
    for (const key of Object.keys(params)) {
      result[key] = normalizeValue(params[key]);
    }
    return result;
  }

  // Si viene algo raro, lo forzamos a string
//   return normalizeValue(params);
  return [normalizeValue(params)] as any;
}
